import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  useLazyGetMeQuery,
  useLogInMutation,
} from '@/app/services/authentication';
import {
  AuthenticationActions,
  AuthenticationType,
} from '@/app/slices/authentication';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';

export const useLogin = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { me } = useSelector(
    (state: { authentication: AuthenticationType }) => state.authentication,
  );

  const navigate = useNavigate();

  const [logIn, { isLoading: isLogInLoading, isError: isLogInError }] =
    useLogInMutation();
  const [
    meQuery,
    {
      isLoading: isMeQueryLoading,
      isFetching: isMeQueryFetching,
      isError: isMeQueryError,
    },
  ] = useLazyGetMeQuery();

  const isError = isLogInError || isMeQueryError;
  const isLoading = isLogInLoading || isMeQueryLoading || isMeQueryFetching;

  useEffect(() => {
    if (!me?.adUserId) {
      meQuery({}).then(({ data }) => {
        if (data?.me?.adUserId) {
          dispatch({
            type: AuthenticationActions.UPDATE_ME,
            me: data.me,
          });
          dispatch({
            type: AuthenticationActions.UPDATE_IS_CSR,
            payload: data.me?.groups?.some((group: string) =>
              group.toLowerCase().includes('csr'),
            ),
          });
        }
      });
    }
  }, [dispatch, meQuery, navigate, me?.adUserId]);

  const handleLogin = useCallback(
    async (username: string, password: string) => {
      try {
        dispatch(
          addToast({
            severity: 'info',
            summary: t('auth.loginAttemptSummary'),
            detail: t('auth.loginAttemptDetail'),
            life: ToastLife.SHORT,
          }),
        );

        const loginResult = await logIn({ username, password }).unwrap();

        if (loginResult.login) {
          await meQuery({})
            .unwrap()
            .then(async ({ me }) => {
              dispatch({
                type: AuthenticationActions.UPDATE_ME,
                me,
              });
              dispatch(
                addToast({
                  severity: 'success',
                  summary: t('auth.loginSuccessfulSummary'),
                  detail: t('auth.loginSuccessfulDetail', {
                    name: me.displayName,
                  }),
                  life: ToastLife.DEFAULT,
                }),
              );
            });
        } else {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('auth.loginFailedSummary'),
              detail: t('auth.loginFailedDetail'),
              life: ToastLife.LONG,
            }),
          );
        }
      } catch (error: unknown) {
        if (typeof error === 'object' && error !== null && 'status' in error) {
          const statusCode = (error as { status: number }).status;
          const errorMessage =
            'data' in error &&
            typeof error.data === 'object' &&
            error.data !== null &&
            'message' in error.data
              ? String(error.data.message)
              : 'An unknown error occurred';

          dispatch(
            addToast({
              severity: 'error',
              summary: t('auth.loginErrorSummary', { statusCode }),
              detail: errorMessage,
              life: ToastLife.LONG,
            }),
          );

          if (statusCode === 401) {
            dispatch(
              addToast({
                severity: 'error',
                summary: t('auth.authenticationFailedSummary'),
                detail: t('auth.authenticationFailedDetail'),
                life: ToastLife.LONG,
              }),
            );
          } else if (statusCode === 403) {
            dispatch(
              addToast({
                severity: 'error',
                summary: t('auth.accountLockedSummary'),
                detail: t('auth.accountLockedDetail'),
                life: ToastLife.LONG,
              }),
            );
          } else if (statusCode === 0) {
            dispatch(
              addToast({
                severity: 'error',
                summary: t('auth.unableToConnectToServerSummary'),
                detail: t('auth.unableToConnectToServerDetail'),
                life: ToastLife.LONG,
              }),
            );
          }
        } else if (error instanceof Error) {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('auth.loginErrorSummary'),
              detail: error.message,
              life: ToastLife.LONG,
            }),
          );
        } else {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('auth.unknownErrorSummary'),
              detail: t('auth.unknownErrorDetail'),
              life: ToastLife.LONG,
            }),
          );
        }
      }
    },
    [dispatch, t, logIn, meQuery],
  );

  return { handleLogin, isLoading, isError };
};
